import { useState, useEffect, useRef, useCallback } from 'react'
import { get } from 'utils/api-routes.util'

export interface InventoryStatsMetrics {
  total_products: number
  total_stock: number
  total_inventory_value: number
  out_of_stock: number
  low_stock: number
  medium_stock: number
  high_stock: number
}

export interface VelocityMetrics {
  products_with_sales: number
  avg_daily_velocity: number
  high_velocity_products: number
  medium_velocity_products: number
  low_velocity_products: number
}

export interface TurnoverMetrics {
  avg_turnover_ratio: number
  excellent_turnover_products: number
  good_turnover_products: number
  poor_turnover_products: number
  dead_stock_products: number
}

export interface InventoryStats {
  overall_stock_health: {
    score: number
    status: string
    metrics: InventoryStatsMetrics
  }
  velocity_performance: {
    score: number
    status: string
    metrics: VelocityMetrics
  }
  turnover_performance: {
    score: number
    status: string
    metrics: TurnoverMetrics
  }
}

export interface InventoryStatsResponse {
  data: InventoryStats
  meta: {
    period: {
      start_date: string
      end_date: string
    }
    generated_at: string
  }
}

export interface UseInventoryStatsReturn {
  data: InventoryStatsResponse | null
  loading: boolean
  error: string | null
  refetch: () => void
}

export const useInventoryStats = (): UseInventoryStatsReturn => {
  const [data, setData] = useState<InventoryStatsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const subscriptionRef = useRef<any>(null)

  const fetchData = useCallback(() => {
    setLoading(true)
    setError(null)

    const url = '/inventory-dashboard/stats'
    const filters = {}

    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
    }

    subscriptionRef.current = get<InventoryStatsResponse>(
      url,
      filters,
    ).subscribe({
      next: (response) => {
        try {
          const responseData: InventoryStatsResponse | null =
            response.response || response.data
          setData(responseData)
          setError(null)
        } catch (err) {
          setError('Failed to parse response data')
        }
        setLoading(false)
      },
      error: (err) => {
        setError(err.message || 'Failed to fetch inventory stats')
        setLoading(false)
      },
    })
  }, [])

  useEffect(() => {
    fetchData()

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }
    }
  }, [fetchData])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}
